import React from 'react';
import { Tabs, Typography } from 'antd';

import PillarTwoComponentGuide from './PillarTwoComponentGuide';
import type { PillarTwoGuideKey } from './PillarTwoComponentGuide';
import PillarTwoOverviewGuide from './PillarTwoOverviewGuide';

const emptyWorkspace = (label: string) => (
  <section
    aria-label={`${label} workspace`}
    className="assessment-inspect-assessment__pillar-one-component-empty"
  />
);

const componentWorkspace = (
  label: string,
  component: PillarTwoGuideKey,
  workspace?: React.ReactNode,
) => (
  <div className="assessment-inspect-assessment__detail-panel assessment-inspect-assessment__detail-panel--pillar-one">
    {workspace ?? emptyWorkspace(label)}
    <PillarTwoComponentGuide component={component} />
  </div>
);

const portfolioFitWorkspace = (workspace?: React.ReactNode) => (
  <div className="assessment-inspect-assessment__detail-panel assessment-inspect-assessment__detail-panel--pillar-one">
    {workspace ?? emptyWorkspace('Overall portfolio fit')}
    <PillarTwoComponentGuide component="portfolio-fit" />
  </div>
);

const optimalPortfolioWorkspace = (workspace?: React.ReactNode) => (
  <div className="assessment-inspect-assessment__detail-panel">
    {workspace ?? emptyWorkspace('Optimal portfolio')}
  </div>
);

const riskLimitsWorkspace = (
  hasSelectedAttempt: boolean,
  instrumentLimitFingerprintExists?: boolean,
  workspace?: React.ReactNode,
) => {
  const status = !hasSelectedAttempt
    ? {
        description:
          'Select a user and session attempt to check its assessment session link.',
        title: 'Instrument-limit fingerprint: Not checked',
      }
    : instrumentLimitFingerprintExists
      ? {
          description:
            'The selected session link contains the limits_fingerprint key. It normally comes from the latest session-specific optimal-portfolio calculation. It is not a complete snapshot of every scoring limit.',
          title: 'Instrument-limit fingerprint exists: Yes',
        }
      : {
          description:
            'The selected session link contains no limits_fingerprint key. This does not mean that no limits were used; it means no optimizer fingerprint was saved.',
          title: 'Instrument-limit fingerprint exists: No',
        };

  return (
    <div className="assessment-inspect-assessment__detail-panel assessment-inspect-assessment__detail-panel--pillar-one">
      <section
        aria-label="Risk limits workspace"
        className="assessment-inspect-assessment__pillar-one-component-empty assessment-inspect-assessment__risk-limits-workspace"
      >
        {workspace}
        <div
          aria-label="Risk limits developer information"
          className="assessment-inspect-assessment__risk-limits-dev-info"
          role="note"
        >
          <Typography.Text type="secondary">
            <strong>Dev info — {status.title}.</strong> {status.description}
          </Typography.Text>
          <Typography.Text
            className="assessment-inspect-assessment__risk-limits-dev-note"
            type="secondary"
          >
            <strong>Note — </strong>The assessment uses both limits.
            <br />
            <strong>Bond-book limit: </strong>The maximum combined bond exposure
            for one player. PM checks it during trading; the assessment also
            constrains the optimal portfolio with it and uses any breach in
            limit-discipline scoring.
            <br />
            <strong>Legacy rollover-instrument limit: </strong>The
            per-instrument value created by adding the mapped rollover members’
            limits. It sets that instrument’s desired and maximum exposure in
            the optimal portfolio. Pillar 2 also divides the user and optimal
            positions by it before comparing them.
          </Typography.Text>
        </div>
      </section>
      <PillarTwoComponentGuide component="risk-limits" />
    </div>
  );
};

const PILLAR_TWO_TABS = [
  {
    key: 'pillar-2-overview',
    label: 'Pillar 2 overview',
    children: (
      <div className="assessment-inspect-assessment__detail-panel assessment-inspect-assessment__detail-panel--pillar-one">
        {emptyWorkspace('Pillar 2 overview')}
        <PillarTwoOverviewGuide />
      </div>
    ),
  },
  {
    key: 'optimal-portfolio',
    label: 'Optimal Portfolio',
    children: null,
  },
  {
    key: 'overall-portfolio-fit',
    label: 'Overall portfolio fit(35%)',
    children: null,
  },
  {
    key: 'position-direction',
    label: 'Position direction(20%)',
    children: null,
  },
  {
    key: 'position-size',
    label: 'Position size(20%)',
    children: null,
  },
  {
    key: 'option-choice',
    label: 'Option choice(10%)',
    children: null,
  },
  {
    key: 'own-view-consistency',
    label: 'Own-view consistency(7.5%)',
    children: componentWorkspace(
      'Own-view consistency',
      'own-view-consistency',
    ),
  },
  {
    key: 'limit-discipline',
    label: 'Limit discipline(7.5%)',
    children: componentWorkspace('Limit discipline', 'limit-discipline'),
  },
];

type PillarTwoComponentTabsProps = {
  hasSelectedAttempt?: boolean;
  instrumentLimitFingerprintExists?: boolean;
  optionChoice?: React.ReactNode;
  optimalPortfolio?: React.ReactNode;
  portfolioFit?: React.ReactNode;
  positionDirection?: React.ReactNode;
  positionSize?: React.ReactNode;
  riskLimits?: React.ReactNode;
};

const PillarTwoComponentTabs: React.FC<PillarTwoComponentTabsProps> = ({
  hasSelectedAttempt = false,
  instrumentLimitFingerprintExists,
  optionChoice,
  optimalPortfolio,
  portfolioFit,
  positionDirection,
  positionSize,
  riskLimits,
}) => {
  const tabs = React.useMemo(
    () => [
      ...PILLAR_TWO_TABS.map(tab =>
        tab.key === 'optimal-portfolio'
          ? {
              ...tab,
              children: optimalPortfolioWorkspace(optimalPortfolio),
            }
          : tab.key === 'overall-portfolio-fit'
            ? {
                ...tab,
                children: portfolioFitWorkspace(portfolioFit),
              }
            : tab.key === 'position-direction'
              ? {
                  ...tab,
                  children: componentWorkspace(
                    'Position direction',
                    'position-direction',
                    positionDirection,
                  ),
                }
              : tab.key === 'position-size'
                ? {
                    ...tab,
                    children: componentWorkspace(
                      'Position size',
                      'position-size',
                      positionSize,
                    ),
                  }
                : tab.key === 'option-choice'
                  ? {
                      ...tab,
                      children: componentWorkspace(
                        'Option choice',
                        'option-choice',
                        optionChoice,
                      ),
                    }
                  : tab,
      ),
      {
        key: 'risk-limits',
        label: 'Risk limits',
        children: riskLimitsWorkspace(
          hasSelectedAttempt,
          instrumentLimitFingerprintExists,
          riskLimits,
        ),
      },
    ],
    [
      hasSelectedAttempt,
      instrumentLimitFingerprintExists,
      optionChoice,
      optimalPortfolio,
      portfolioFit,
      positionDirection,
      positionSize,
      riskLimits,
    ],
  );

  return (
    <Tabs
      className="assessment-inspect-assessment__pillar-one-component-tabs"
      defaultActiveKey="pillar-2-overview"
      destroyOnHidden
      items={tabs}
    />
  );
};

export default PillarTwoComponentTabs;
